import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const { t, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      const sortedProjects = response.data.sort((a, b) => {
        if (a.title_fr.toLowerCase().includes('25 appartements')) return -1;
        if (b.title_fr.toLowerCase().includes('25 appartements')) return 1;
        return 0;
      });
      setProjects(sortedProjects);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching projects:', error);
      }
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const openGallery = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeGallery = useCallback(() => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  }, []);

  const nextImage = useCallback(() => {
    if (selectedProject && currentImageIndex < selectedProject.images.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  }, [selectedProject, currentImageIndex]);

  const prevImage = useCallback(() => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  }, [currentImageIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) return;
      if (e.key === 'ArrowRight') nextImage();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'Escape') closeGallery();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, nextImage, prevImage, closeGallery]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) nextImage();
    else if (distance < -minSwipeDistance) prevImage();
  };

  return (
    <section id="projects" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <AnimateOnScroll animation="fade-up">
            <p className="text-neutral-500 text-sm tracking-[0.3em] uppercase mb-4">
              {t({ fr: 'Nos Réalisations', en: 'Our Work' })}
            </p>
            <h2 className="text-5xl md:text-7xl font-light text-neutral-900 leading-[1.1]">
              {t({ fr: 'Projets', en: 'Projects' })}
              <br />
              <span className="text-neutral-400">{t({ fr: 'Sélectionnés', en: 'Selected' })}</span>
            </h2>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fade-up" delay={1}>
            <p className="text-neutral-500 max-w-md mt-8 md:mt-0 text-lg">
              {t({ 
                fr: 'Une sélection de projets porteurs de sens',
                en: 'A selection of meaningful projects'
              })}
            </p>
          </AnimateOnScroll>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-2 border-neutral-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <AnimateOnScroll 
                key={project.id} 
                animation={index % 2 === 0 ? "fade-right" : "fade-left"}
                delay={index % 3}
              >
                <div
                  onClick={() => openGallery(project)}
                  className={`group cursor-pointer transform transition-all duration-700 hover:scale-[1.02] ${index % 3 === 0 ? 'md:row-span-2' : ''}`}
                >
                  <div className={`relative overflow-hidden bg-neutral-100 ${index % 3 === 0 ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                    <img
                      src={project.images[0] || 'https://images.unsplash.com/photo-1506863571123-04bd1ac882c1'}
                      alt={language === 'fr' ? project.title_fr : project.title_en}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500"></div>
                    
                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500 group-hover:translate-y-[-10px]">
                      <p className="text-neutral-300 text-xs tracking-[0.2em] uppercase mb-2 transform transition-all duration-500 opacity-70 group-hover:opacity-100">
                        {t(translations.projects.categories[project.category])}
                      </p>
                      <h3 className="text-2xl md:text-3xl font-light text-white mb-2 transform transition-all duration-500">
                        {language === 'fr' ? project.title_fr : project.title_en}
                      </h3>
                      {project.images.length > 1 && (
                        <p className="text-white/50 text-sm transform transition-all duration-500 opacity-0 group-hover:opacity-100">
                          {project.images.length} {t({ fr: 'photos', en: 'photos' })}
                        </p>
                      )}
                    </div>

                    {/* Hover Arrow */}
                    <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-4 group-hover:translate-x-0">
                      <ArrowRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        )}
      </div>

      {/* Image Gallery Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
          <button
            onClick={closeGallery}
            className="absolute top-6 right-6 text-neutral-400 hover:text-neutral-900 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="w-full max-w-6xl px-6">
            <div className="text-center mb-8">
              <p className="text-neutral-500 text-xs tracking-[0.2em] uppercase mb-2">
                {t(translations.projects.categories[selectedProject.category])}
              </p>
              <h2 className="text-3xl font-light text-neutral-900">
                {language === 'fr' ? selectedProject.title_fr : selectedProject.title_en}
              </h2>
            </div>

            <div 
              className="relative"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={selectedProject.images[currentImageIndex]}
                alt={`${language === 'fr' ? selectedProject.title_fr : selectedProject.title_en} - ${currentImageIndex + 1}`}
                className="w-full h-[60vh] object-contain"
                draggable="false"
              />

              {selectedProject.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    disabled={currentImageIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-neutral-300 text-neutral-400 hover:text-neutral-900 hover:border-neutral-900 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    disabled={currentImageIndex === selectedProject.images.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-neutral-300 text-neutral-400 hover:text-neutral-900 hover:border-neutral-900 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-neutral-400 text-sm">
                {currentImageIndex + 1} / {selectedProject.images.length}
              </div>
            </div>

            {selectedProject.images.length > 1 && (
              <div className="mt-6 flex gap-2 justify-center overflow-x-auto pb-2">
                {selectedProject.images.map((image, index) => (
                  <button
                    key={`${selectedProject.id}-${image}`}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 overflow-hidden border transition-all ${
                      currentImageIndex === index ? 'border-neutral-500' : 'border-neutral-200 opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <p className="text-center text-neutral-400 text-sm mt-6 hidden md:block">
              {t({ fr: 'Utilisez les flèches ← → pour naviguer', en: 'Use ← → arrow keys to navigate' })}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
